import { useContext } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { HallMaintenanceTask, HallMaintenanceTaskCategory, HallMaintenanceFrequencyUnit } from '../../domain/HallMaintenanceTask';
import { useStores } from '../../stores';
import { withLazyMounting } from '../../components/hoc/LazyDialog';
import { AbilityContext } from '../../components/Can';

interface Props {
    isOpen: boolean;
    task: HallMaintenanceTask;
    onClose: () => void;
    onDelete: (id: number) => void;
}

interface FormValues {
    id?: number;
    title: string;
    cardNumber?: string;
    subTitle?: string;
    warningText?: string;
    category: HallMaintenanceTaskCategory;
    description?: string;
    frequencyUnit: HallMaintenanceFrequencyUnit;
    frequencyInterval: number;
    frequencyLabel?: string;
    plannedMonths: number[];
}

const months = moment.monthsShort();
const categories = [
    HallMaintenanceTaskCategory.OutdoorInspection,
    HallMaintenanceTaskCategory.IndoorInspection,
    HallMaintenanceTaskCategory.DoorsAndWindows,
    HallMaintenanceTaskCategory.FireProtectionSystem,
    HallMaintenanceTaskCategory.WaterSupply,
    HallMaintenanceTaskCategory.Heating,
    HallMaintenanceTaskCategory.Ventilation,
    HallMaintenanceTaskCategory.PowerSupply,
    HallMaintenanceTaskCategory.Lighting,
    HallMaintenanceTaskCategory.Tools,
    HallMaintenanceTaskCategory.Equipment,
];

const HallMaintenanceTaskDialog = observer((props: Props) => {
    const { t } = useTranslation();
    const { hallMaintenanceStore } = useStores();
    const ability = useContext(AbilityContext);
    const canManage = ability.can('manage', 'HallMaintenance');
    const isEdit = !!props.task;

    const initialValues: FormValues = {
        title: '',
        cardNumber: '',
        category: HallMaintenanceTaskCategory.OutdoorInspection,
        frequencyUnit: HallMaintenanceFrequencyUnit.Month,
        frequencyInterval: 1,
        plannedMonths: [],
    };

    if (props.task) {
        initialValues.id = props.task.id;
        initialValues.title = props.task.title;
        initialValues.cardNumber = props.task.cardNumber;
        initialValues.subTitle = props.task.subTitle;
        initialValues.warningText = props.task.warningText;
        initialValues.category = props.task.category;
        initialValues.description = props.task.description;
        initialValues.frequencyUnit = props.task.frequencyUnit;
        initialValues.frequencyInterval = props.task.frequencyInterval;
        initialValues.frequencyLabel = props.task.frequencyLabel;
        initialValues.plannedMonths = props.task.plannedMonths || [];
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        onSubmit: (values) => {
            if (!canManage) {
                props.onClose();
                return;
            }

            const payload = {
                title: values.title,
                cardNumber: values.cardNumber,
                subTitle: values.subTitle,
                warningText: values.warningText,
                category: values.category,
                description: values.description,
                frequencyUnit: values.frequencyUnit,
                frequencyInterval: Number(values.frequencyInterval) || 1,
                frequencyLabel: values.frequencyLabel,
                plannedMonths: values.plannedMonths,
            };

            if (props.task) {
                hallMaintenanceStore.updateTask(props.task.id, payload, props.onClose);
            } else {
                hallMaintenanceStore.createTask(payload, props.onClose);
            }
        },
    });

    const handleMonthChange = (event: any) => {
        const {
            target: { value },
        } = event;
        const monthsValue = typeof value === 'string' ? value.split(',').map((val: string) => Number(val)) : value;
        formik.setFieldValue('plannedMonths', monthsValue);
    };

    return (
        <Dialog open={props.isOpen} fullWidth maxWidth="md" onClose={props.onClose}>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>{isEdit ? t("task") : t("create_hall_maintenance_task")}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                margin="normal"
                                id="title"
                                name="title"
                                label={t("title")}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={formik.values.title || ''}
                                onChange={formik.handleChange}
                                required
                                InputProps={{ readOnly: !canManage }}
                            />
                            <TextField
                                margin="normal"
                                id="cardNumber"
                                name="cardNumber"
                                label={t("card_number")}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={formik.values.cardNumber || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                            <TextField
                                margin="normal"
                                id="subTitle"
                                name="subTitle"
                                label={t("subtitle")}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={formik.values.subTitle || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                            <TextField
                                margin="normal"
                                id="warningText"
                                name="warningText"
                                label={t("warning_text")}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={formik.values.warningText || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl margin="normal" fullWidth variant="standard" disabled={!canManage}>
                                <InputLabel id="category-label">{t("category")}</InputLabel>
                                <Select
                                    labelId="category-label"
                                    id="category"
                                    name="category"
                                    value={formik.values.category}
                                    onChange={formik.handleChange}
                                    label={t("category")}
                                >
                                    {categories.map((category) => (
                                        <MenuItem key={category} value={category}>{t(category)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl margin="normal" fullWidth variant="standard" disabled={!canManage}>
                                <InputLabel id="frequency-unit-label">{t("frequency_unit")}</InputLabel>
                                <Select
                                    labelId="frequency-unit-label"
                                    id="frequencyUnit"
                                    name="frequencyUnit"
                                    value={formik.values.frequencyUnit}
                                    onChange={formik.handleChange}
                                    label={t("frequency_unit")}
                                >
                                    <MenuItem value={HallMaintenanceFrequencyUnit.Month}>{t(HallMaintenanceFrequencyUnit.Month)}</MenuItem>
                                    <MenuItem value={HallMaintenanceFrequencyUnit.Year}>{t(HallMaintenanceFrequencyUnit.Year)}</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                margin="normal"
                                id="frequencyInterval"
                                name="frequencyInterval"
                                label={t("frequency_interval")}
                                type="number"
                                fullWidth
                                variant="standard"
                                value={formik.values.frequencyInterval}
                                onChange={(event) => formik.setFieldValue('frequencyInterval', Number(event.target.value))}
                                inputProps={{ min: 1 }}
                                InputProps={{ readOnly: !canManage }}
                            />
                            <TextField
                                margin="normal"
                                id="frequencyLabel"
                                name="frequencyLabel"
                                label={t("frequency_label")}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={formik.values.frequencyLabel || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                id="description"
                                name="description"
                                label={t("description")}
                                type="text"
                                fullWidth
                                multiline
                                minRows={3}
                                variant="standard"
                                value={formik.values.description || ''}
                                onChange={formik.handleChange}
                                InputProps={{ readOnly: !canManage }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth margin="normal" variant="standard" disabled={!canManage}>
                                <InputLabel id="planned-months-label">{t("planned_months")}</InputLabel>
                                <Select
                                    labelId="planned-months-label"
                                    id="plannedMonths"
                                    multiple
                                    value={formik.values.plannedMonths || []}
                                    onChange={handleMonthChange}
                                    renderValue={(selected) => (
                                        (selected as number[]).map(month => months[(month - 1 + 12) % 12]).join(', ')
                                    )}
                                >
                                    {months.map((name, index) => (
                                        <MenuItem key={index + 1} value={index + 1}>
                                            <Checkbox checked={(formik.values.plannedMonths || []).indexOf(index + 1) > -1} />
                                            <ListItemText primary={name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {isEdit && canManage && <Button color="warning" onClick={() => props.onDelete(props.task!.id)}>{t("delete")}</Button>}
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    {canManage && <Button color="primary" type="submit">{t("submit")}</Button>}
                </DialogActions>
            </form>
        </Dialog>
    );
});

export default withLazyMounting(HallMaintenanceTaskDialog);
